// Расчёт отчётов скоростных шкал (Струп, SDMT).
// Струп: подсчёт — выравнивание последовательностей (alignOps), как в ревью
// Claude/ChatGPT: ok — совпало, sub — ошибка, del — не услышано, ins — лишнее.
// Клиент больше не судит об ошибках — каскад ложных ошибок невозможен.
import { localToday } from '../lib/cortivoxApi';

function findPayload(rawAnswers, keys) {
  for (const v of Object.values(rawAnswers || {})) {
    let p = v;
    if (typeof v === 'string') {
      try { p = JSON.parse(v); } catch { continue; }
    }
    if (p && typeof p === 'object' && keys.every(k => k in p)) return p;
  }
  return null;
}

function alignOps(expected, heard) {
  const n = expected.length, m = heard.length;
  const cost = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = 1; i <= n; i++) cost[i][0] = i;
  for (let j = 1; j <= m; j++) cost[0][j] = j;
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      cost[i][j] = Math.min(
        cost[i - 1][j - 1] + (expected[i - 1] === heard[j - 1] ? 0 : 1),
        cost[i - 1][j] + 1,
        cost[i][j - 1] + 1,
      );
    }
  }
  const ops = []; let i = n, j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && cost[i][j] === cost[i - 1][j - 1] + (expected[i - 1] === heard[j - 1] ? 0 : 1)) {
      ops.push(expected[i - 1] === heard[j - 1] ? 'ok' : 'sub'); i--; j--;
    } else if (i > 0 && cost[i][j] === cost[i - 1][j] + 1) { ops.push('del'); i--; }
    else { ops.push('ins'); j--; }
  }
  return ops.reverse();
}

export function buildStroopReport(rawAnswers, patientProfile, scaleId, scaleData) {
  const p = findPayload(rawAnswers, ['times', 'heard']);
  if (!p) return null;

  const times = p.times.map(Number);
  if (times.some(t => !t || t <= 0)) return null;

  const stroopQ = (scaleData?.questions || []).find(q => q.type === 'stroop');
  const cards = stroopQ?.cards || [];
  const heardArr = Array.isArray(p.heard) ? p.heard : [];
  const endedBy = Array.isArray(p.endedBy) ? p.endedBy : ['auto', 'auto', 'auto'];

  const per = [0, 1, 2].map(k => {
    const expected = cards[k]?.expected || [];
    const heard = (heardArr[k] || []).map(x => (typeof x === 'string' ? x : x.c));
    const ops = alignOps(expected, heard);
    const cnt = op => ops.filter(o => o === op).length;
    // Время речи: от первого до последнего финального слова — задержка
    // старта и константная ASR-задержка вычитаются сами
    const tape = heardArr[k] || [];
    const span = tape.length > 1 && tape[tape.length - 1].t != null
      ? Math.round((tape[tape.length - 1].t - tape[0].t) * 10) / 10
      : times[k];
    return { ok: cnt('ok'), sub: cnt('sub'), del: cnt('del'), ins: cnt('ins'),
             heard: heard.length, total: expected.length, span, wall: times[k] };
  });

  const rate = (ok, t) => (t > 0 ? ok / t : 0);
  const W  = Math.min(100, Math.round(45 * rate(per[0].ok, per[0].span)));
  const C  = Math.min(50,  Math.round(45 * rate(per[1].ok, per[1].span)));
  const CW = Math.min(50,  Math.round(45 * rate(per[2].ok, per[2].span)));
  const IG = W + C > 0 ? Math.round(10 * (CW - (W * C) / (W + C))) / 10 : null;

  const perItemB = per[1].span / 50, perItemC = per[2].span / 50;
  const slowdown = perItemB > 0 ? Math.round(100 * perItemC / perItemB) / 100 : null;

  const totalDel = per.reduce((s, x) => s + x.del, 0);
  const totalWords = per.reduce((s, x) => s + x.total, 0);
  const lostShare = totalWords > 0 ? Math.round(1000 * totalDel / totalWords) / 10 : 0;
  const quality = lostShare > 5 ? 'bad' : lostShare > 2 ? 'warn' : 'ok';

  return {
    kind: 'speed', scaleId, scaleName: 'Тест Струпа',
    patientName: patientProfile?.name || 'Гость',
    date: localToday(),
    algorithm: 'выравнивание v1 · клиент · предварительный подсчёт',
    raw: { times, per, endedBy },
    golden: { W, C, CW, IG },
    slowdown, quality, lostShare,
  };
}

// SDMT: бэкенд (score_sdmt) ждёт {score, errors, omissions, intervals} в
// rawAnswers['1a'] — компонент шлёт этот же формат. Отчёт показывает их.
export function buildSdmtReport(rawAnswers, patientProfile, scaleId) {
  const p = findPayload(rawAnswers, ['score']);
  if (!p) return null;

  const score = Number(p.score) || 0;
  const errors = Number(p.errors) || 0;
  const omissions = Number(p.omissions) || 0;
  const intervals = Array.isArray(p.intervals) ? p.intervals.map(Number) : null;

  // Форма прохождения: 'oral' (устно) или 'written' (ручной ввод), пишет CodingV2.
  // Старые записи без поля form — 'unknown'.
  const form = p.form === 'oral' || p.form === 'written' ? p.form : 'unknown';

  // Ориентировочные пороги (устная форма, популяция РС) — заглушка до
  // подключения регрессионных норм (Fellows 2020) и локальных норм.
  // Применяются ТОЛЬКО к устной форме: для ручного ввода пороги не заданы, цветовой
  // вердикт не выносится (предварительный z по форме приходит с сервера, см. SdmtBody).
  // ИСПРАВИТЬ ПОСЛЕ ПИЛОТА.
  let band;
  if (form !== 'oral') {
    band = {
      label: form === 'written'
        ? 'Пороги для ручного ввода не заданы'
        : 'Форма не указана: оценка по порогам невозможна',
      cls: 'neutral',
    };
  } else if (score >= 55) band = { label: 'Норма', cls: 'green' };
  else if (score >= 45) band = { label: 'Пограничное снижение', cls: 'yellow' };
  else if (score >= 37) band = { label: 'Когнитивное нарушение (лёгкое)', cls: 'orange' };
  else band = { label: 'Выраженное когнитивное нарушение', cls: 'red' };

  return {
    kind: 'speed', scaleId,
    scaleName: `SDMT — ${scaleId === 'sdmt_v2' ? 'вариант 2' : 'вариант 1'}`,
    patientName: patientProfile?.name || 'Гость',
    date: localToday(),
    algorithm: form === 'oral'
      ? 'клиентский показ · пороги ориентировочные (РС, устная форма)'
      : 'клиентский показ · пороги для этой формы не заданы',
    raw: { score, errors, omissions, intervals, form },
    band,
  };
}